import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { getToken, getCurrentApiUrl, Match } from '../api/client';

type MatchUpdateCallback = (match: Match) => void;

/**
 * Hook to subscribe to match updates via WebSocket.
 * Used on match summary/status pages to show real-time updates when players join/leave.
 */
export function useMatchSubscription(
  matchId: string | undefined,
  onMatchUpdate: MatchUpdateCallback
) {
  const socketRef = useRef<Socket | null>(null);
  const callbackRef = useRef(onMatchUpdate);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = onMatchUpdate;
  }, [onMatchUpdate]);

  useEffect(() => {
    if (!matchId) {
      return;
    }

    const token = getToken();
    if (!token) {
      console.error('No auth token available for match subscription socket');
      return;
    }

    const socket = io(getCurrentApiUrl(), {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Match subscription socket connected for:', matchId);
      socket.emit('match:subscribe', matchId);
    });

    socket.on('connect_error', (error) => {
      console.error('Match subscription socket connection error:', error);
    });

    socket.on('match:updated', (data: { match: Match }) => {
      console.log('Received match update:', data.match.id);
      callbackRef.current(data.match);
    });

    socket.on('match:deleted', () => {
      console.log('Match was deleted');
      // Could navigate away or show message
    });

    return () => {
      if (socket) {
        socket.emit('match:unsubscribe', matchId);
        socket.disconnect();
      }
    };
  }, [matchId]);
}
